import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";

import Pagination from "../view/common/pagination/Pagination";
import { ApiResponse } from "../models/countrytypes";
import { deleteData, getData, patchData, postData } from "../api/ApiServices";
import { State, stateResponse } from "../models/statetypes";
import TableComponent from "./TableComponent";
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";

import customStyles from "../styles/currencyMaster.module.css";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";

const StateMaster: React.FC = () => {
  const [stateData, setStateData] = useState<State[]>([]);
  const [filteredStateData, setFilteredStateData] = useState<State[]>([]);
  const [newState, setNewState] = useState<State>({
    gstCode: "",
    stateName: "",
    status: "ACTIVE",
  });
  const [editingState, setEditingState] = useState<State | null>(null);
  const [originalEditState, setOriginalEditState] = useState<State | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [validationErrors, setValidationErrors] = useState<{
    gstCode?: string;
    stateName?: string;
    status?: string;
  }>({});

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions);

  const [initializestateMaster, isstateMasterInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isstateMasterInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializestateMaster) {
      fetchStates();
    }
  }, [initializestateMaster, currentPage, itemsPerPage, searchTerm, filters]);

  useEffect(() => {
    filterStates();
  }, [searchTerm, stateData]);

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const fetchStates = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<stateResponse>>(
        "/statemaster",
        {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          filter: filters,
        }
      );

      setStateData(response?.data?.data || []);
      setTotalItems(response?.data?.pagination.total);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      setError("Failed to fetch states.");
      console.error("Error fetching states:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterStates = () => {
    if (!searchTerm) {
      setFilteredStateData(stateData);
      return;
    }
    const filteredData = stateData.filter(
      (s) =>
        s.gstCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.stateName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStateData(filteredData);
    setCurrentPage(1);
  };

  const hasStateChanged = (): boolean => {
    if (!editingState || !originalEditState) return false;

    return (
      editingState.gstCode !== originalEditState.gstCode ||
      editingState.stateName !== originalEditState.stateName ||
      editingState.status !== originalEditState.status
    );
  };

  const validateForm = (): boolean => {
    const errors: { gstCode?: string; stateName?: string; status?: string } = {};

    if (
      (!editingState && !newState.gstCode.trim()) ||
      (editingState && !editingState.gstCode.trim())
    ) {
      errors.gstCode = "GST Code is required";
    }

    if (
      (!editingState && !newState.stateName.trim()) ||
      (editingState && !editingState.stateName.trim())
    ) {
      errors.stateName = "State Name is required";
    }

    if (editingState && !editingState.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddOrEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (editingState && !hasStateChanged()) {
      configureAlert(
        "No changes were made.",
        () => {
          closeAlert();
          setIsModalOpen(false);
          setEditingState(null);
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }

    setIsLoading(true);
    try {
      if (editingState) {
        let updatedState = { ...editingState };

        const response = await patchData<ApiResponse<State>>(
          "/statemaster",
          editingState._id!, 
          updatedState
        );

        setIsModalOpen(false);
        setEditingState(null);
        setOriginalEditState(null);

        configureAlert(
          `${editingState.stateName} has been updated successfully.`,
          () => {
            fetchStates();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const response = await postData<ApiResponse<stateResponse>>(
          "/statemaster",
          newState
        );

        setIsModalOpen(false);
        setNewState({ gstCode: "", stateName: "", status: "ACTIVE" });

        configureAlert(
          `${newState.stateName} has been added successfully.`,
          () => {
            fetchStates();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
    } catch (error: any) {
      console.error("Error saving state:", error);

      configureAlert(
        error.message || `Failed to ${editingState ? "update" : "add"} state.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteState = async (id: string) => {
    const stateToDelete = stateData.find((state) => state._id === id);

    if (stateToDelete) {
      configureAlert(
        `Are you sure you want to Inactive ${stateToDelete.stateName}?`,
        async () => {
          closeAlert();

          try {
            await deleteData("/statemaster", id);

            configureAlert(
              `State has been Inactive successfully.`,
              () => {
                fetchStates();
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting state:", error);
            configureAlert(
              "Failed to delete state.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handleModalClose = () => {
    if (
      isModalOpen &&
      ((editingState &&
        (editingState.gstCode.trim() !== "" ||
          editingState.stateName.trim() !== "")) ||
        (!editingState &&
          (newState.gstCode.trim() !== "" || newState.stateName.trim() !== "")))
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          setEditingState(null);
          setOriginalEditState(null);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
      setEditingState(null);
      setOriginalEditState(null);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isEditing: boolean
  ) => {
    const { name, value } = e.target;

    if (isEditing && editingState) {
      setEditingState({ ...editingState, [name]: value });
    } else {
      setNewState({ ...newState, [name]: value });
    }

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    if (editingState) {
      setEditingState((prev) => ({
        ...prev!,
        [name]: selectedOption?.value || "",
      }));
    } else {
      setNewState((prev) => ({
        ...prev,
        [name]: selectedOption?.value || "",
      }));
    }

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleAddClick = () => {
    setEditingState(null);
    setOriginalEditState(null);
    setNewState({ gstCode: "", stateName: "", status: "ACTIVE" });
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const columns = [
    { key: "gstCode", label: "GST Code", sortable: true },
    { key: "stateName", label: "State Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <h3 className="breadcrumb-nav">
              <span>General</span>
              <span>State Master</span>
            </h3>
            {permission.menus.statemaster.create ? (
              <div
                className="items-button-container tooltip-wrapper"
                onClick={handleAddClick}
              >
                <button className="item-models-add-btn">
                  <Plus size={16} />
                </button>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>

      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={stateData}
            onEdit={(state) => {
              setNewState({ gstCode: "", stateName: "", status: "ACTIVE" });

              setEditingState(state);
              setOriginalEditState({ ...state });
              setValidationErrors({});
              setIsModalOpen(true);
            }}
            onDelete={handleDeleteState}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />

        {isModalOpen && (
          <div className="item-models-modal-overlay">
            <div className="item-models-modal">
              <div className="item-models-modal-header">
                <h5>{editingState ? "Edit State" : "Add New State"}</h5>
                <button
                  className="item-models-modal-close"
                  onClick={handleModalClose}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleAddOrEdit} noValidate>
                <div className={customStyles["form-group"]}>
                  <input
                    type="text"
                    name="gstCode"
                    placeholder="GST Code"
                    value={
                      editingState ? editingState.gstCode : newState.gstCode
                    }
                    onChange={(e) => handleInputChange(e, !!editingState)}
                    className={
                      validationErrors.gstCode
                        ? customStyles["input-error"]
                        : ""
                    }
                    required
                  />
                  {validationErrors.gstCode && (
                    <div className={customStyles["form-error-message"]}>
                      {validationErrors.gstCode}
                    </div>
                  )}
                </div>

                <div className={customStyles["form-group"]}>
                  <input
                    type="text"
                    name="stateName"
                    placeholder="State Name"
                    value={
                      editingState ? editingState.stateName : newState.stateName
                    }
                    onChange={(e) => handleInputChange(e, !!editingState)}
                    className={
                      validationErrors.stateName
                        ? customStyles["input-error"]
                        : ""
                    }
                    required
                  />
                  {validationErrors.stateName && (
                    <div className={customStyles["form-error-message"]}>
                      {validationErrors.stateName}
                    </div>
                  )}
                </div>

                {editingState && (
                  <div className="select-container">
                    <Select
                      name="status"
                      options={statusOptions}
                      value={statusOptions.find(
                        (option) => option.value === editingState.status
                      )}
                      onChange={handleSelectChange}
                      placeholder="Select Status"
                      className={validationErrors.status ? "select-error" : ""}
                      classNamePrefix="react-select"
                    />
                    {validationErrors.status && (
                      <div className={customStyles["form-error-message"]}>
                        {validationErrors.status}
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="submit-button"
                >
                  {isLoading ? "Saving..." : editingState ? "Update" : "Add"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default StateMaster;
